import { mergeConfig, enabledTabs, resolveConfig } from './config';
import { fetchWidgetContext } from './context-api';
import {
    decorateCustomTriggerElements,
    ensureCustomTriggerStyles,
    removeCustomTriggerStyles,
} from './custom-trigger-style';
import {
    WIDGET_PANEL_HEIGHT_PX,
    WIDGET_PANEL_WIDTH_PX,
} from './dimensions';
import {
    identifyWidgetVisitorByEmail,
    submitWidgetFeedback
    
} from './feedback-api';
import type {WidgetFeedbackDraft} from './feedback-api';
import {
    buildFeedbackDescriptionHtml,
    validateFeedbackImageFiles,
} from './feedback-description';
import {
    contrastTextColor,
    resolveFloatingButtonStyle,
} from './floating-launcher-style';
import {
    SVG_ACTION_BUG,
    SVG_ACTION_FEATURE,
    SVG_ACTION_GENERAL,
    SVG_ACTION_IMPROVE,
    SVG_HUB_CHANGELOG_CLOCK,
    SVG_HUB_DOCS,
    SVG_HUB_ROADMAP,
} from './hub-icons';
import {
    isLauncherIconPresetId,
    LAUNCHER_ICON_PRESETS,
} from './launcher-icon-presets';
import {
    VOTIQ_LOGO_MARK_LAUNCHER_SVG,
    VOTIQ_LOGO_MARK_MONO_SVG,
    VOTIQ_LOGO_MARK_SVG,
} from './logo';
import { MOCK_CHANGELOG, MOCK_FEEDBACK, MOCK_ROADMAP } from './mock-data';
import { fetchWidgetEmbedConfig } from './settings-api';
import { WIDGET_CSS, WIDGET_STYLE_ID } from './styles';
import type {
    ResolvedConfig,
    VotiqContextOptions,
    VotiqInitOptions,
    VotiqInstance,
    WidgetContextDoc,
    WidgetContextResponse,
    WidgetTab,
} from './types';
import {
    clearWidgetSessionToken,
    readWidgetSessionToken,
    readWidgetVisitorEmail,
    writeWidgetSessionToken,
    writeWidgetVisitorEmail,
} from './widget-session';

const ICON_MESSAGE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>';
const ICON_CLOSE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"><path d="M18 6 6 18M6 6l12 12"/></svg>';
const ICON_CHECK_SUCCESS =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const ICON_CHEVRON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m18 15-6-6-6 6"/></svg>';
const ICON_CHEVRON_RIGHT =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
const ICON_BACK =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"><path d="m15 18-6-6 6-6"/></svg>';
const ICON_IMAGE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
const ICON_BOOK =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/></svg>';
const ICON_EXTERNAL =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';


type BodyScreen =
    | 'hub'
    | 'list'
    | 'feedback-detail'
    | 'feedback-new'
    | 'feedback-identify'
    | 'feedback-thanks'
    | 'roadmap-detail';
const TYPE_LABELS: Record<string, string> = {
    feature_request: 'Feature',
    bug: 'Bug',
    improvement: 'Improvement',
};

const COLUMN_LABELS: Record<string, string> = {
    planned: 'Planned',
    in_progress: 'In progress',
    shipped: 'Shipped',
};

const ENTRY_TYPE_LABELS: Record<string, string> = {
    new: 'Added',
    improved: 'Changed',
    deprecated: 'Deprecated',
    removed: 'Removed',
    fixed: 'Fixed',
    security: 'Security',
};

function ensureStyles(): void {
    if (document.getElementById(WIDGET_STYLE_ID)) {
        return;
    }

    const style = document.createElement('style');
    style.id = WIDGET_STYLE_ID;
    style.textContent = WIDGET_CSS;
    document.head.appendChild(style);
}

function applyPreviewContainerLayout(config: ResolvedConfig): void {
    if (!config.preview || !config.container) {
        return;
    }

    const container = config.container;

    if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
    }

    container.style.overflow =
        config.previewScope === 'launcher' ? 'visible' : 'hidden';
}

export class VotiqWidget implements VotiqInstance {
    private config: ResolvedConfig;
    private root: HTMLElement;
    private mountTarget: HTMLElement;
    private stackEl: HTMLElement;
    private panelEl: HTMLElement | null = null;
    private launcherEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private panelOpen = false;
    private activeTab: WidgetTab = 'feedback';
    private voteState: Record<string, { count: number; voted: boolean }> = {};
    private bodyScreen: BodyScreen = 'hub';
    private screenTransition: 'forward' | 'back' = 'forward';
    private shouldAnimateScreen = false;
    private wasPanelOpen = false;
    private selectedId: string | null = null;
    private feedbackDraftType:
        | 'feature_request'
        | 'bug'
        | 'improvement'
        | null = null;
    private feedbackDraft: WidgetFeedbackDraft | null = null;
    private feedbackSubmitting = false;
    private feedbackSubmitError: string | null = null;
    private feedbackFormError: string | null = null;
    private contextPayload: VotiqContextOptions = {};
    private contextData: WidgetContextResponse | null = null;
    private contextLoading = false;
    private contextError: string | null = null;
    private pendingContext: VotiqContextOptions | string | null = null;
    private customTriggerHandler: ((event: Event) => void) | null = null;
    private layoutConfigSyncHandler: (() => void) | null = null;

    constructor(options: VotiqInitOptions) {
        this.config = resolveConfig(options);
        this.root = document.createElement('div');
        this.root.id = 'votiq-widget-root';
        this.mountTarget = this.config.container ?? document.body;

        if (this.config.preview) {
            this.root.classList.add('votiq-root--preview');
            this.syncPreviewScopeClasses();
        }

        this.stackEl = document.createElement('div');
        this.stackEl.className = `votiq-stack votiq-stack--${this.config.position}`;

        applyPreviewContainerLayout(this.config);

        if (options.context) {
            this.pendingContext = options.context;
        }
    }

    mount(): void {
        ensureStyles();
        this.syncCustomTriggerStyleSheet();
        this.render();
        this.syncPreviewSizeVars();
        this.mountTarget.appendChild(this.root);
        this.syncCustomTriggerBinding();
        this.startLayoutConfigSync();

        if (this.pendingContext) {
            const pending = this.pendingContext;
            this.pendingContext = null;
            void this.applyContext(pending, { focusHelp: false });
        }
    }

    context(options: VotiqContextOptions | string): void {
        void this.applyContext(options, { focusHelp: true });
    }

    open(): void {
        this.panelOpen = true;
        this.bodyScreen = 'hub';
        this.screenTransition = 'forward';
        this.selectedId = null;
        this.requestScreenAnimation();
        this.render();
    }

    close(): void {
        this.panelOpen = false;
        this.render();
    }

    toggle(): void {
        this.panelOpen = !this.panelOpen;

        if (this.panelOpen) {
            this.bodyScreen = 'hub';
            this.screenTransition = 'forward';
            this.selectedId = null;
            this.requestScreenAnimation();
        }

        this.render();
    }

    isOpen(): boolean {
        return this.panelOpen;
    }

    private emitPreviewPanelState(): void {
        if (
            !this.config.preview ||
            this.config.previewScope !== 'panel' ||
            !this.config.container
        ) {
            return;
        }

        this.config.container.dispatchEvent(
            new CustomEvent('votiq-preview-panel-state', {
                bubbles: true,
                detail: { open: this.panelOpen },
            }),
        );
    }

    private syncPreviewScopeClasses(): void {
        this.root.classList.remove(
            'votiq-root--preview-scope-panel',
            'votiq-root--preview-scope-launcher',
        );

        if (!this.config.preview) {
            return;
        }

        if (this.config.previewScope === 'panel') {
            this.root.classList.add('votiq-root--preview-scope-panel');
        } else if (this.config.previewScope === 'launcher') {
            this.root.classList.add('votiq-root--preview-scope-launcher');
        }
    }

    private syncPreviewSizeVars(): void {
        if (!this.config.preview || !this.config.container) {
            return;
        }

        const container = this.config.container;
        const computed = getComputedStyle(container);

        for (const name of [
            '--votiq-preview-h',
            '--votiq-preview-w',
            '--votiq-preview-chrome',
            '--votiq-preview-inset',
        ]) {
            const value = computed.getPropertyValue(name).trim();

            if (value) {
                this.root.style.setProperty(name, value);
            }
        }
    }

    update(patch: Partial<VotiqInitOptions>): void {
        const next = mergeConfig(this.config, patch);
        const containerChanged =
            next.container !== this.config.container ||
            next.preview !== this.config.preview;
        const layoutChanged =
            (patch.buttonType !== undefined &&
                patch.buttonType !== this.config.buttonType) ||
            (patch.position !== undefined &&
                patch.position !== this.config.position) ||
            (patch.previewScope !== undefined &&
                patch.previewScope !== this.config.previewScope);

        this.config = next;

        if (containerChanged || (this.config.preview && layoutChanged)) {
            this.destroy();
            this.root = document.createElement('div');
            this.root.id = 'votiq-widget-root';

            if (this.config.preview) {
                this.root.classList.add('votiq-root--preview');
                this.syncPreviewScopeClasses();
            }

            this.stackEl = document.createElement('div');
            this.stackEl.className = `votiq-stack votiq-stack--${this.config.position}`;
            this.mountTarget = this.config.container ?? document.body;

            applyPreviewContainerLayout(this.config);

            this.mount();

            return;
        }

        const tabs = enabledTabs(this.config);

        if (!tabs.includes(this.activeTab)) {
            this.activeTab = tabs[0] ?? 'help';
        }

        if (patch.context) {
            void this.applyContext(patch.context, { focusHelp: true });
        } else {
            this.syncPreviewScopeClasses();
            applyPreviewContainerLayout(this.config);
            this.syncCustomTriggerStyleSheet();
            this.render();
            this.syncPreviewSizeVars();
            this.syncCustomTriggerBinding();
        }
    }

    private normalizeContextOptions(
        options: VotiqContextOptions | string,
    ): VotiqContextOptions {
        if (typeof options === 'string') {
            return { context: options };
        }

        return options;
    }

    private async applyContext(
        options: VotiqContextOptions | string,
        { focusHelp }: { focusHelp: boolean },
    ): Promise<void> {
        this.contextPayload = this.normalizeContextOptions(options);
        this.contextLoading = true;
        this.contextError = null;
        this.contextData = null;

        const tabs = enabledTabs(this.config);

        if (focusHelp && tabs.includes('help')) {
            this.activeTab = 'help';
            this.bodyScreen = 'list';
            this.selectedId = null;
        }

        this.render();

        try {
            this.contextData = await fetchWidgetContext(
                this.config.projectId,
                this.contextPayload,
                this.config.apiBaseUrl || undefined,
            );

            if (focusHelp && tabs.includes('help')) {
                this.activeTab = 'help';
            }
        } catch (error) {
            this.contextError =
                error instanceof Error
                    ? error.message
                    : 'Could not load contextual help.';
        } finally {
            this.contextLoading = false;
            this.render();
            this.syncPreviewSizeVars();
        }
    }

    destroy(): void {
        this.stopLayoutConfigSync();
        this.unbindCustomTriggers();
        removeCustomTriggerStyles();
        this.root.remove();
        this.panelEl = null;
        this.launcherEl = null;
        this.bodyEl = null;
    }

    private syncCustomTriggerStyleSheet(): void {
        if (this.config.buttonType === 'custom' && !this.config.preview) {
            ensureCustomTriggerStyles(this.config.customButton);
            decorateCustomTriggerElements();
        } else {
            removeCustomTriggerStyles();
        }
    }

    private syncCustomTriggerBinding(): void {
        const shouldBind =
            this.config.buttonType === 'custom' &&
            this.config.enabled &&
            !this.config.preview;

        if (shouldBind) {
            this.bindCustomTriggers();
        } else {
            this.unbindCustomTriggers();
        }

        this.syncCustomTriggerStyleSheet();
    }

    private async refreshLayoutFromServer(): Promise<void> {
        if (this.config.preview || !this.config.projectId) {
            return;
        }

        try {
            const remote = await fetchWidgetEmbedConfig(
                this.config.projectId,
                this.config.apiBaseUrl || undefined,
            );

            this.config = mergeConfig(this.config, {
                buttonType: remote.buttonType,
                position: remote.position,
                floatingIcon: remote.floatingIcon,
                launcherIconPreset: remote.launcherIconPreset,
                customLauncherIconUrl: remote.customLauncherIconUrl,
                darkLauncher: remote.darkLauncher,
                floatingButton: remote.floatingButton,
                customButton: remote.customButton,
                launcherLabel: remote.launcherLabel,
                welcomeTitle: remote.welcomeTitle,
                welcomeMessage: remote.welcomeMessage,
                tabs: remote.tabs,
                theme: remote.theme,
                enabled: remote.enabled,
            });

            this.render();

            this.syncCustomTriggerStyleSheet();

            if (remote.buttonType !== 'custom') {
                this.unbindCustomTriggers();
            } else if (remote.enabled) {
                this.bindCustomTriggers();
            }
        } catch {
            // Keep current layout if the config request fails.
        }
    }

    private startLayoutConfigSync(): void {
        if (this.config.preview || this.layoutConfigSyncHandler) {
            return;
        }

        this.layoutConfigSyncHandler = () => {
            if (document.visibilityState === 'visible') {
                void this.refreshLayoutFromServer();
            }
        };

        document.addEventListener(
            'visibilitychange',
            this.layoutConfigSyncHandler,
        );
    }

    private stopLayoutConfigSync(): void {
        if (!this.layoutConfigSyncHandler) {
            return;
        }

        document.removeEventListener(
            'visibilitychange',
            this.layoutConfigSyncHandler,
        );
        this.layoutConfigSyncHandler = null;
    }

    private bindCustomTriggers(): void {
        if (this.customTriggerHandler) {
            return;
        }

        this.customTriggerHandler = (event: Event) => {
            const target = event.target;

            if (!(target instanceof Element)) {
                return;
            }

            if (!target.closest('[data-votiq]')) {
                return;
            }

            event.preventDefault();
            this.toggle();
        };

        document.addEventListener('click', this.customTriggerHandler, true);
    }

    private unbindCustomTriggers(): void {
        if (!this.customTriggerHandler) {
            return;
        }

        document.removeEventListener('click', this.customTriggerHandler, true);
        this.customTriggerHandler = null;
    }

    private render(): void {
        this.root.replaceChildren();
        this.stackEl.replaceChildren();
        this.root.appendChild(this.stackEl);
        this.stackEl.className = `votiq-stack votiq-stack--${this.config.position}`;

        if (!this.config.enabled) {
            return;
        }

        const tabs = enabledTabs(this.config);

        if (tabs.length === 0) {
            return;
        }

        const scope = this.config.previewScope;
        const showPanel = scope !== 'launcher' && this.panelOpen;
        const showLauncher =
            scope !== 'panel' && this.config.buttonType !== 'custom';

        const openingPanel = showPanel && !this.wasPanelOpen;

        if (showPanel) {
            this.panelEl = this.buildPanel(tabs);

            if (openingPanel) {
                this.panelEl.classList.add('votiq-panel--mount');
                this.requestScreenAnimation();
            }

            this.stackEl.appendChild(this.panelEl);
        }

        this.wasPanelOpen = this.panelOpen;

        if (showLauncher) {
            this.launcherEl = this.buildLauncher();
            this.stackEl.appendChild(this.launcherEl);
        }

        this.emitPreviewPanelState();
    }

    private appendLauncherIcon(btn: HTMLElement, compact: boolean): void {
        const markClass = compact
            ? 'votiq-launcher-mark votiq-launcher-mark--sm'
            : 'votiq-launcher-mark';

        if (this.config.floatingIcon === 'votiq') {
            const wrap = document.createElement('span');
            wrap.className = markClass;
            wrap.innerHTML =
                this.config.buttonType === 'floating'
                    ? VOTIQ_LOGO_MARK_LAUNCHER_SVG
                    : VOTIQ_LOGO_MARK_SVG;
            btn.appendChild(wrap);

            return;
        }

        if (this.config.floatingIcon === 'custom') {
            const presetId = this.config.launcherIconPreset;

            if (isLauncherIconPresetId(presetId)) {
                const wrap = document.createElement('span');
                wrap.className = markClass;
                wrap.innerHTML = LAUNCHER_ICON_PRESETS[presetId];
                btn.appendChild(wrap);

                return;
            }

            if (this.config.customLauncherIconUrl) {
                const img = document.createElement('img');
                img.src = this.config.customLauncherIconUrl;
                img.alt = '';
                img.className = compact
                    ? 'votiq-launcher-img votiq-launcher-img--sm'
                    : 'votiq-launcher-img';
                btn.appendChild(img);

                return;
            }
        }

        const wrap = document.createElement('span');
        wrap.className = markClass;
        wrap.innerHTML = ICON_MESSAGE;
        btn.appendChild(wrap);
    }

    private buildLauncher(): HTMLElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        const isSticky = this.config.buttonType === 'sticky';

        if (isSticky) {
            const isBottom =
                this.config.position === 'left-bottom' ||
                this.config.position === 'right-bottom';
            const brandClass = this.config.darkLauncher
                ? 'votiq-launcher--sticky-tab-dark'
                : 'votiq-launcher--sticky-tab-brand';

            if (isBottom) {
                const edge =
                    this.config.position === 'left-bottom' ? 'left' : 'right';
                btn.className = `votiq-launcher votiq-launcher--sticky-bar votiq-launcher--sticky-bar-${edge} ${brandClass}`;
                const label = document.createElement('span');
                label.className = 'votiq-launcher-bar-label';
                label.textContent = this.config.launcherLabel;
                btn.appendChild(label);
            } else {
                const edge = this.config.position.startsWith('left')
                    ? 'left'
                    : 'right';
                btn.className = `votiq-launcher votiq-launcher--sticky-tab votiq-launcher--sticky-tab-${edge} ${brandClass}`;
                const label = document.createElement('span');
                label.className = 'votiq-launcher-tab-label';
                label.textContent = this.config.launcherLabel;
                btn.appendChild(label);
            }
        } else {
            const floatingStyle = resolveFloatingButtonStyle(
                this.config.floatingButton,
            );
            btn.className = 'votiq-launcher votiq-launcher--styled';
            btn.style.backgroundColor = floatingStyle.color;
            btn.style.color = contrastTextColor(floatingStyle.color);
            btn.style.setProperty(
                '--votiq-launcher-icon-size',
                `${floatingStyle.iconSize}px`,
            );
            this.appendLauncherIcon(btn, false);
        }

        btn.setAttribute('aria-label', this.config.launcherLabel);
        btn.setAttribute('aria-expanded', String(this.panelOpen));
        btn.addEventListener('click', () => {
            if (
                this.config.preview &&
                this.config.previewScope === 'launcher'
            ) {
                this.config.container?.dispatchEvent(
                    new CustomEvent('votiq-preview-toggle-panel', {
                        bubbles: true,
                    }),
                );

                return;
            }

            this.toggle();
        });

        return btn;
    }

    private getPanelTitle(): string {
        if (this.bodyScreen === 'hub') {
            return 'Share your feedback';
        }

        if (this.bodyScreen === 'feedback-new' && this.feedbackDraftType) {
            const labels: Record<string, string> = {
                feature_request: 'Feature request',
                bug: 'Report a bug',
                improvement: 'General feedback',
            };

            return labels[this.feedbackDraftType] ?? 'New feedback';
        }

        if (this.bodyScreen === 'feedback-identify') {
            return 'Almost done';
        }

        if (this.bodyScreen === 'feedback-thanks') {
            return 'Thank you';
        }

        if (this.bodyScreen === 'feedback-detail') {
            const item = MOCK_FEEDBACK.find((row) => row.id === this.selectedId);

            return item?.title ?? 'Feedback';
        }

        if (this.bodyScreen === 'roadmap-detail') {
            const item = MOCK_ROADMAP.find((row) => row.id === this.selectedId);

            return item?.title ?? 'Roadmap';
        }

        if (this.activeTab === 'help') {
            return 'Knowledge Base';
        }

        if (this.activeTab === 'roadmap') {
            return 'Roadmap';
        }

        if (this.activeTab === 'updates') {
            return 'Changelog';
        }

        if (this.activeTab === 'feedback') {
            return 'Feedback';
        }

        return 'Votiq';
    }

    private buildPanelNavFooter(): HTMLElement | null {
        const navItems: {
            tab: WidgetTab;
            label: string;
            icon: string;
            mod: string;
        }[] = [];

        if (this.config.showHelp) {
            navItems.push({
                tab: 'help',
                label: 'Knowledge Base',
                icon: SVG_HUB_DOCS,
                mod: 'docs',
            });
        }

        if (this.config.showRoadmap) {
            navItems.push({
                tab: 'roadmap',
                label: 'Roadmap',
                icon: SVG_HUB_ROADMAP,
                mod: 'roadmap',
            });
        }

        if (this.config.showChangelog) {
            navItems.push({
                tab: 'updates',
                label: 'Changelog',
                icon: SVG_HUB_CHANGELOG_CLOCK,
                mod: 'changelog',
            });
        }

        if (navItems.length === 0) {
            return null;
        }

        const footer = document.createElement('footer');
        footer.className = 'votiq-panel-nav';
        const tablist = document.createElement('div');
        tablist.className = 'votiq-panel-nav-list';
        tablist.setAttribute('role', 'tablist');

        for (const item of navItems) {
            const isActive =
                this.bodyScreen !== 'hub' && this.activeTab === item.tab;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = `votiq-panel-nav-btn votiq-panel-nav-btn--${item.mod}${
                isActive ? ' votiq-panel-nav-btn--active' : ''
            }`;
            button.setAttribute('role', 'tab');
            button.setAttribute('aria-selected', String(isActive));
            button.innerHTML = `
        <span class="votiq-panel-nav-btn-icon" aria-hidden="true">${item.icon}</span>
        <span class="votiq-panel-nav-btn-label">${escapeHtml(item.label)}</span>
      `;
            button.addEventListener('click', () => {
                if (this.bodyScreen === 'hub') {
                    this.openHubTab(item.tab);

                    return;
                }

                if (this.activeTab === item.tab) {
                    this.goToHub();

                    return;
                }

                this.openHubTab(item.tab);
            });
            tablist.appendChild(button);
        }

        footer.appendChild(tablist);

        const brand = document.createElement('a');
        brand.href = 'https://votiq.co.uk';
        brand.target = '_blank';
        brand.rel = 'noopener noreferrer';
        brand.className = 'votiq-panel-brand';
        brand.innerHTML = `
      <span class="votiq-panel-brand-logo">${VOTIQ_LOGO_MARK_MONO_SVG}</span>
      <span class="votiq-panel-brand-text">Powered by Votiq</span>
    `;
        footer.appendChild(brand);

        return footer;
    }

    private getBodyClassName(): string {
        if (this.bodyScreen === 'hub') {
            return 'votiq-body votiq-body--hub';
        }

        if (
            this.bodyScreen === 'feedback-new' ||
            this.bodyScreen === 'feedback-identify' ||
            this.bodyScreen === 'feedback-thanks'
        ) {
            return 'votiq-body votiq-body--form';
        }

        return 'votiq-body votiq-body--scroll';
    }

    private shouldShowPanelNav(): boolean {
        return (
            this.bodyScreen !== 'feedback-new' &&
            this.bodyScreen !== 'feedback-identify' &&
            this.bodyScreen !== 'feedback-thanks'
        );
    }

    private buildPanel(tabs: WidgetTab[]): HTMLElement {
        const panel = document.createElement('div');
        panel.className = `votiq-panel votiq-panel--${this.config.theme}`;
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', 'Votiq widget');
        panel.style.width = `${WIDGET_PANEL_WIDTH_PX}px`;
        panel.style.height = `${WIDGET_PANEL_HEIGHT_PX}px`;
        panel.style.boxSizing = 'border-box';
        panel.style.flexShrink = '0';

        const header = document.createElement('header');
        header.className = 'votiq-panel-head';

        const backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className = 'votiq-close votiq-panel-back';
        backBtn.setAttribute(
            'aria-label',
            this.bodyScreen === 'hub' ? 'Close widget' : 'Back',
        );
        backBtn.innerHTML = ICON_BACK;
        backBtn.addEventListener('click', () => {
            if (this.bodyScreen === 'hub') {
                this.close();

                return;
            }

            if (
                this.bodyScreen === 'feedback-identify' ||
                this.bodyScreen === 'feedback-thanks'
            ) {
                this.screenTransition = 'back';
                this.bodyScreen = 'hub';
                this.feedbackDraft = null;
                this.feedbackSubmitError = null;
                this.requestScreenAnimation();
                this.render();

                return;
            }

            this.screenTransition = 'back';
            this.goToHub();
        });

        const title = document.createElement('p');
        title.className = 'votiq-panel-head-title';
        title.textContent = this.getPanelTitle();

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'votiq-close votiq-panel-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = ICON_CLOSE;
        closeBtn.addEventListener('click', () => {
            this.bodyScreen = 'hub';
            this.close();
        });

        header.appendChild(backBtn);
        header.appendChild(title);
        header.appendChild(closeBtn);
        panel.appendChild(header);

        this.bodyEl = document.createElement('div');
        this.bodyEl.className = this.getBodyClassName();
        this.renderBody(this.bodyEl, tabs);
        panel.appendChild(this.bodyEl);

        requestAnimationFrame(() => {
            if (!this.shouldAnimateScreen) {
                return;
            }

            this.shouldAnimateScreen = false;
            this.playScreenTransition(this.bodyEl, header);
        });

        if (this.shouldShowPanelNav()) {
            const navFooter = this.buildPanelNavFooter();

            if (navFooter) {
                panel.appendChild(navFooter);
            }
        }

        return panel;
    }

    private renderHub(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-hub';

        if (this.config.showFeedback) {
            const primary = document.createElement('div');
            primary.className = 'votiq-hub-primary';

            const feedbackActions: {
                label: string;
                icon: string;
                mod: 'feature' | 'bug' | 'general';
                type: 'feature_request' | 'bug' | 'improvement';
            }[] = [
                {
                    label: 'Feature Request',
                    icon: SVG_ACTION_FEATURE,
                    mod: 'feature',
                    type: 'feature_request',
                },
                {
                    label: 'Report a Bug',
                    icon: SVG_ACTION_BUG,
                    mod: 'bug',
                    type: 'bug',
                },
                {
                    label: 'General Feedback',
                    icon: SVG_ACTION_GENERAL,
                    mod: 'general',
                    type: 'improvement',
                },
            ];

            for (const action of feedbackActions) {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = `votiq-share-card votiq-share-card--${action.mod}`;
                button.innerHTML = `
        <span class="votiq-share-card-icon votiq-share-card-icon--${action.mod}" aria-hidden="true">${action.icon}</span>
        <span class="votiq-share-card-title">${escapeHtml(action.label)}</span>
        <span class="votiq-share-card-chevron" aria-hidden="true">${ICON_CHEVRON_RIGHT}</span>
      `;
                button.addEventListener('click', () => {
                    this.openFeedbackDraft(action.type);
                });
                primary.appendChild(button);
            }

            wrap.appendChild(primary);
        }

        return wrap;
    }

    private requestScreenAnimation(): void {
        this.shouldAnimateScreen = true;
    }

    private mountScreen(body: HTMLElement, content: HTMLElement): void {
        const screen = document.createElement('div');
        screen.className = `votiq-screen votiq-screen--${this.screenTransition}`;
        screen.appendChild(content);
        body.appendChild(screen);
    }

    private playScreenTransition(
        body: HTMLElement | null,
        header: HTMLElement,
    ): void {
        const screen = body?.querySelector('.votiq-screen');

        if (screen) {
            screen.classList.remove('votiq-screen--animate');
            void screen.getBoundingClientRect();
            screen.classList.add('votiq-screen--animate');
        }

        header.classList.remove('votiq-panel-head--animate');
        void header.getBoundingClientRect();
        header.classList.add('votiq-panel-head--animate');
    }

    private renderBody(body: HTMLElement, tabs: WidgetTab[]): void {
        body.replaceChildren();
        body.className = this.getBodyClassName();

        let content: HTMLElement | null = null;

        if (this.bodyScreen === 'hub') {
            content = this.renderHub();
        } else if (
            this.bodyScreen === 'feedback-detail' &&
            this.activeTab === 'feedback' &&
            tabs.includes('feedback')
        ) {
            content = this.renderFeedbackDetail();
        } else if (
            this.bodyScreen === 'feedback-new' &&
            this.activeTab === 'feedback' &&
            tabs.includes('feedback')
        ) {
            content = this.renderFeedbackNew();
        } else if (
            this.bodyScreen === 'feedback-identify' &&
            this.activeTab === 'feedback' &&
            tabs.includes('feedback')
        ) {
            content = this.renderFeedbackIdentify();
        } else if (
            this.bodyScreen === 'feedback-thanks' &&
            this.activeTab === 'feedback' &&
            tabs.includes('feedback')
        ) {
            content = this.renderFeedbackThanks();
        } else if (
            this.bodyScreen === 'roadmap-detail' &&
            this.activeTab === 'roadmap' &&
            tabs.includes('roadmap')
        ) {
            content = this.renderRoadmapDetail();
        } else if (this.activeTab === 'help' && tabs.includes('help')) {
            content = this.renderHelp();
        } else if (this.activeTab === 'feedback' && tabs.includes('feedback')) {
            content = this.renderFeedback();
        } else if (this.activeTab === 'roadmap' && tabs.includes('roadmap')) {
            content = this.renderRoadmap();
        } else if (this.activeTab === 'updates' && tabs.includes('updates')) {
            content = this.renderUpdates();
        }

        if (content) {
            this.mountScreen(body, content);
        }
    }

    private renderHelp(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-help-screen';
        const head = document.createElement('div');
        head.className = 'votiq-help-head';
        const label = this.contextData?.label ?? this.contextPayload.context ?? null;

        head.innerHTML = `
      <span class="votiq-list-toolbar-label">Help</span>
      ${label ? `<span class="votiq-help-context-pill">${escapeHtml(label)}</span>` : ''}
    `;
        wrap.appendChild(head);

        if (this.contextLoading) {
            const loading = document.createElement('p');
            loading.className = 'votiq-help-empty';
            loading.textContent = 'Loading help for this screen…';
            wrap.appendChild(loading);

            return wrap;
        }

        if (this.contextError) {
            const error = document.createElement('p');
            error.className = 'votiq-help-empty votiq-help-empty--error';
            error.textContent = this.contextError;
            wrap.appendChild(error);

            return wrap;
        }

        const docs = this.contextData?.documentation ?? [];

        if (docs.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'votiq-help-empty';
            empty.textContent = this.contextPayload.context
                ? `No public help articles for “${this.contextPayload.context}” yet.`
                : 'Call Votiq.context({ context: "your-screen" }) from your app to load help.';
            wrap.appendChild(empty);

            return wrap;
        }

        const list = document.createElement('div');
        list.className = 'votiq-help-list';

        for (const doc of docs) {
            list.appendChild(this.buildHelpDocCard(doc));
        }

        wrap.appendChild(list);

        return wrap;
    }

    private buildHelpDocCard(doc: WidgetContextDoc): HTMLElement {
        const link = document.createElement('a');
        link.className = 'votiq-help-card';
        link.href = doc.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.innerHTML = `
      <span class="votiq-help-card-icon" aria-hidden="true">${ICON_BOOK}</span>
      <span class="votiq-help-card-body">
        <span class="votiq-help-card-kicker">Guide</span>
        <span class="votiq-help-card-title">${escapeHtml(doc.title)}</span>
        ${doc.summary ? `<span class="votiq-help-card-summary">${escapeHtml(doc.summary)}</span>` : ''}
      </span>
      <span class="votiq-help-card-external" aria-hidden="true">${ICON_EXTERNAL}</span>
    `;

        return link;
    }

    private goToHub(): void {
        this.screenTransition = 'back';
        this.bodyScreen = 'hub';
        this.selectedId = null;
        this.feedbackDraftType = null;
        this.requestScreenAnimation();
        this.render();
    }

    private openHubTab(tab: WidgetTab): void {
        if (this.openPortalPage(tab)) {
            return;
        }

        this.screenTransition = 'forward';
        this.activeTab = tab;
        this.bodyScreen = 'list';
        this.selectedId = null;
        this.requestScreenAnimation();
        this.render();
    }

    private openFeedbackDraft(
        type: 'feature_request' | 'bug' | 'improvement',
    ): void {
        this.screenTransition = 'forward';
        this.feedbackDraftType = type;
        this.activeTab = 'feedback';
        this.bodyScreen = 'feedback-new';
        this.selectedId = null;
        this.requestScreenAnimation();
        this.render();
    }

    private openFeedbackIdentify(): void {
        this.screenTransition = 'forward';
        this.bodyScreen = 'feedback-identify';
        this.requestScreenAnimation();
        this.render();
    }

    private getWidgetGoogleAuthUrl(): string | null {
        const base = this.config.apiBaseUrl?.replace(/\/$/, '');

        if (!base || !this.config.projectId) {
            return null;
        }

        return `${base}/api/widget/v1/projects/${encodeURIComponent(this.config.projectId)}/auth/google`;
    }

    private getPortalPageUrl(tab: WidgetTab): string | null {
        const pagePathByTab: Partial<Record<WidgetTab, string>> = {
            help: 'docs',
            roadmap: 'roadmap',
            updates: 'changelog',
        };
        const pagePath = pagePathByTab[tab];

        if (!pagePath) {
            return null;
        }

        const rawBase = this.config.apiBaseUrl?.trim();
        const base =
            rawBase && rawBase !== ''
                ? rawBase.replace(/\/$/, '')
                : window.location.origin;

        return `${base}/p/${encodeURIComponent(this.config.projectId)}/${pagePath}`;
    }

    private openPortalPage(tab: WidgetTab): boolean {
        const url = this.getPortalPageUrl(tab);

        if (!url) {
            return false;
        }

        const newTab = window.open(url, '_blank', 'noopener,noreferrer');

        if (!newTab) {
            window.location.assign(url);
        }

        return true;
    }

    private emitFeedbackIdentified(
        method: 'email' | 'google' | 'skip',
        email?: string,
    ): void {
        this.config.container?.dispatchEvent(
            new CustomEvent('votiq-feedback-identified', {
                bubbles: true,
                detail: {
                    method,
                    email: email ?? null,
                    feedbackType: this.feedbackDraftType,
                    projectId: this.config.projectId,
                },
            }),
        );
    }

    private completeFeedbackIdentify(
        method: 'email' | 'google' | 'skip',
        email?: string,
    ): void {
        this.emitFeedbackIdentified(method, email);
        this.goToHub();
    }

    private renderFeedback(): HTMLElement {
        const wrap = document.createElement('div');

        const toolbar = document.createElement('div');
        toolbar.className = 'votiq-list-toolbar';
        const listLabel = document.createElement('span');
        listLabel.className = 'votiq-list-toolbar-label';
        listLabel.textContent = 'Send feedback';
        toolbar.appendChild(listLabel);
        wrap.appendChild(toolbar);

        const actions: {
            label: string;
            icon: string;
            mod: 'feature' | 'bug' | 'improve';
        }[] = [
            { label: 'Request feature', icon: SVG_ACTION_FEATURE, mod: 'feature' },
            { label: 'Report bug', icon: SVG_ACTION_BUG, mod: 'bug' },
            { label: 'Suggest improvement', icon: SVG_ACTION_IMPROVE, mod: 'improve' },
        ];

        const actionList = document.createElement('div');
        actionList.className = 'votiq-feature-actions';

        for (const action of actions) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'votiq-feature-btn';
            button.innerHTML = `
        <span class="votiq-feature-btn-icon votiq-feature-btn-icon--${action.mod}" aria-hidden="true">${action.icon}</span>
        <span class="votiq-feature-btn-text">${action.label}</span>
      `;
            button.addEventListener('click', () => {
                this.openFeedbackDraft(
                    action.mod === 'feature'
                        ? 'feature_request'
                        : action.mod === 'bug'
                          ? 'bug'
                          : 'improvement',
                );
            });
            actionList.appendChild(button);
        }

        wrap.appendChild(actionList);

        return wrap;
    }

    private renderFeedbackDetail(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-detail';
        const item = MOCK_FEEDBACK.find((i) => i.id === this.selectedId);

        if (!item) {
            this.goToHub();

            return wrap;
        }

        if (!this.voteState[item.id]) {
            this.voteState[item.id] = {
                count: item.votesCount,
                voted: item.voted,
            };
        }

        const state = this.voteState[item.id];
        const panel = document.createElement('div');
        panel.className = 'votiq-detail-panel';
        panel.innerHTML = `
      <span class="votiq-card-meta">${TYPE_LABELS[item.type] ?? item.type}</span>
      <h3 class="votiq-detail-title">${escapeHtml(item.title)}</h3>
      <p class="votiq-detail-desc">${escapeHtml(item.description ?? 'No description yet.')}</p>
    `;

        const voteRow = document.createElement('button');
        voteRow.type = 'button';
        voteRow.className = `votiq-detail-vote ${state.voted ? 'votiq-vote--active' : ''}`;
        voteRow.innerHTML = `${ICON_CHEVRON}<span>${state.voted ? 'Voted' : 'Upvote'} · ${state.count}</span>`;
        voteRow.addEventListener('click', () => {
            state.voted = !state.voted;
            state.count += state.voted ? 1 : -1;
            this.render();
        });

        wrap.appendChild(panel);
        wrap.appendChild(voteRow);

        return wrap;
    }

    private renderFeedbackNew(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-detail votiq-detail--feedback-form';

        const panel = document.createElement('div');
        panel.className = 'votiq-detail-panel votiq-detail-panel--feedback-form';
        panel.innerHTML = `
      <label class="votiq-field votiq-field--first">
        <span class="votiq-field-label">Title</span>
        <input class="votiq-field-input" type="text" placeholder="Short summary" />
      </label>
      <label class="votiq-field votiq-field--grow">
        <span class="votiq-field-label">Details</span>
        <textarea class="votiq-field-input votiq-field-textarea" rows="2" placeholder="Add context…"></textarea>
      </label>
    `;

        const attachRow = document.createElement('div');
        attachRow.className = 'votiq-attach-row';

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;
        fileInput.className = 'votiq-attach-input';

        const attachBtn = document.createElement('button');
        attachBtn.type = 'button';
        attachBtn.className = 'votiq-attach-btn';
        attachBtn.setAttribute('aria-label', 'Add image');
        attachBtn.innerHTML = ICON_IMAGE;

        const attachCount = document.createElement('span');
        attachCount.className = 'votiq-attach-count';
        attachCount.hidden = true;

        attachBtn.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', () => {
            const count = fileInput.files?.length ?? 0;
            attachCount.hidden = count === 0;
            attachCount.textContent = String(count);
        });

        attachRow.appendChild(fileInput);
        attachRow.appendChild(attachBtn);
        attachRow.appendChild(attachCount);
        panel.appendChild(attachRow);

        if (this.feedbackFormError) {
            const formError = document.createElement('p');
            formError.className = 'votiq-identify-error';
            formError.textContent = this.feedbackFormError;
            panel.appendChild(formError);
        }

        const submit = document.createElement('button');
        submit.type = 'button';
        submit.className = 'votiq-cta votiq-cta--block';
        submit.textContent = this.feedbackSubmitting
            ? 'Submitting…'
            : 'Submit';
        submit.disabled = this.feedbackSubmitting;
        submit.addEventListener('click', () => {
            void this.handleFeedbackFormSubmit(panel, submit, fileInput);
        });

        wrap.appendChild(panel);
        wrap.appendChild(submit);

        return wrap;
    }

    private async handleFeedbackFormSubmit(
        panel: HTMLElement,
        submit: HTMLButtonElement,
        fileInput: HTMLInputElement,
    ): Promise<void> {
        if (this.feedbackSubmitting || !this.feedbackDraftType) {
            return;
        }

        const titleInput = panel.querySelector<HTMLInputElement>(
            'input[type="text"]',
        );
        const detailsInput = panel.querySelector<HTMLTextAreaElement>(
            'textarea',
        );
        const title = titleInput?.value.trim() ?? '';
        const detailsText = detailsInput?.value.trim() ?? '';
        const files = fileInput.files
            ? Array.from(fileInput.files)
            : [];

        if (!title) {
            this.feedbackFormError = null;
            titleInput?.focus();
            this.render();

            return;
        }

        const imageError = validateFeedbackImageFiles(files);

        if (imageError) {
            this.feedbackFormError = imageError;
            this.render();

            return;
        }

        this.feedbackFormError = null;
        this.feedbackSubmitError = null;

        try {
            const descriptionHtml = await buildFeedbackDescriptionHtml(
                detailsText,
                files,
            );

            this.feedbackDraft = {
                type: this.feedbackDraftType,
                title,
                description: descriptionHtml,
                context: this.resolveFeedbackContext(),
            };
        } catch (error) {
            this.feedbackFormError =
                error instanceof Error
                    ? error.message
                    : 'Could not process images. Try again.';
            this.render();

            return;
        }

        if (this.config.preview) {
            this.openFeedbackIdentify();

            return;
        }

        const existingToken = readWidgetSessionToken(this.config.projectId);

        if (existingToken) {
            await this.submitFeedbackDraft(existingToken);

            return;
        }

        this.openFeedbackIdentify();
    }

    private resolveFeedbackContext(): string | null {
        const key =
            this.contextPayload.context ??
            this.contextPayload.page ??
            this.contextPayload.feature ??
            null;

        return typeof key === 'string' && key.trim() !== '' ? key.trim() : null;
    }

    private renderFeedbackIdentify(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-identify';

        const lead = document.createElement('p');
        lead.className = 'votiq-identify-lead';
        const rememberedEmail = readWidgetVisitorEmail(this.config.projectId);
        lead.textContent = rememberedEmail
            ? 'Confirm your email to send this feedback.'
            : 'Enter your email so we can follow up on your feedback.';

        const emailForm = document.createElement('form');
        emailForm.className = 'votiq-identify-form';
        emailForm.noValidate = true;

        const emailLabel = document.createElement('label');
        emailLabel.className = 'votiq-field votiq-field--first';
        emailLabel.innerHTML = `
      <span class="votiq-field-label">Email</span>
      <input class="votiq-field-input" type="email" name="email" autocomplete="email" placeholder="you@company.com" required />
    `;

        const emailError = document.createElement('p');
        emailError.className = 'votiq-identify-error';
        emailError.hidden = true;

        const emailSubmit = document.createElement('button');
        emailSubmit.type = 'submit';
        emailSubmit.className = 'votiq-cta votiq-cta--block';
        emailSubmit.textContent = 'Continue with email';

        emailForm.appendChild(emailLabel);
        emailForm.appendChild(emailError);
        emailForm.appendChild(emailSubmit);

        const emailInput = emailForm.querySelector<HTMLInputElement>(
            'input[name="email"]',
        );

        if (emailInput && rememberedEmail) {
            emailInput.value = rememberedEmail;
        }

        emailForm.addEventListener('submit', (event) => {
            event.preventDefault();

            if (this.feedbackSubmitting) {
                return;
            }

            const input = emailForm.querySelector<HTMLInputElement>(
                'input[name="email"]',
            );
            const value = input?.value.trim() ?? '';

            if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                emailError.hidden = false;
                emailError.textContent = 'Enter a valid email address.';

                return;
            }

            emailError.hidden = true;
            void this.completeFeedbackIdentifyWithEmail(value, {
                emailSubmit,
                emailError,
            });
        });

        wrap.appendChild(lead);
        wrap.appendChild(emailForm);

        if (this.feedbackSubmitError) {
            const submitError = document.createElement('p');
            submitError.className = 'votiq-identify-error';
            submitError.textContent = this.feedbackSubmitError;
            wrap.appendChild(submitError);
        }

        return wrap;
    }

    private async completeFeedbackIdentifyWithEmail(
        email: string,
        controls: {
            emailSubmit: HTMLButtonElement;
            emailError: HTMLElement;
        },
    ): Promise<void> {
        if (!this.feedbackDraft) {
            controls.emailError.hidden = false;
            controls.emailError.textContent =
                'Your feedback draft was lost. Please go back and try again.';

            return;
        }

        if (this.config.preview) {
            this.emitFeedbackIdentified('email', email);
            this.feedbackDraft = null;
            this.openFeedbackThanks();

            return;
        }

        this.feedbackSubmitting = true;
        this.feedbackSubmitError = null;
        controls.emailSubmit.disabled = true;
        controls.emailSubmit.textContent = 'Submitting…';
        this.render();

        try {
            const existingToken = readWidgetSessionToken(
                this.config.projectId,
            );

            if (existingToken) {
                await this.submitFeedbackDraft(existingToken, email);

                return;
            }

            const identify = await identifyWidgetVisitorByEmail(
                this.config.projectId,
                email,
                this.config.apiBaseUrl || undefined,
            );

            writeWidgetSessionToken(
                this.config.projectId,
                identify.sessionToken,
            );
            writeWidgetVisitorEmail(this.config.projectId, email);

            await this.submitFeedbackDraft(identify.sessionToken, email);
        } catch (error) {
            this.feedbackSubmitError =
                error instanceof Error
                    ? error.message
                    : 'Could not submit feedback. Try again.';
            this.render();
        } finally {
            this.feedbackSubmitting = false;
            controls.emailSubmit.disabled = false;
            controls.emailSubmit.textContent = 'Continue with email';
        }
    }

    private async submitFeedbackDraft(
        sessionToken: string,
        email?: string,
    ): Promise<void> {
        if (!this.feedbackDraft) {
            return;
        }

        this.feedbackSubmitting = true;
        this.feedbackSubmitError = null;
        this.render();

        try {
            await submitWidgetFeedback(
                this.config.projectId,
                sessionToken,
                this.feedbackDraft,
                this.config.apiBaseUrl || undefined,
            );

            if (email) {
                writeWidgetVisitorEmail(this.config.projectId, email);
            }

            writeWidgetSessionToken(this.config.projectId, sessionToken);
            this.emitFeedbackIdentified('email', email);
            this.feedbackDraft = null;
            this.feedbackSubmitError = null;
            this.openFeedbackThanks();
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Could not submit feedback. Try again.';

            if (message.toLowerCase().includes('session')) {
                clearWidgetSessionToken(this.config.projectId);
                this.feedbackSubmitError =
                    'Your session expired. Please enter your email again.';
                this.openFeedbackIdentify();

                return;
            }

            if (this.bodyScreen === 'feedback-identify') {
                this.feedbackSubmitError = message;
            } else {
                this.feedbackFormError = message;
            }

            this.render();
        } finally {
            this.feedbackSubmitting = false;
        }
    }

    private openFeedbackThanks(): void {
        this.screenTransition = 'forward';
        this.bodyScreen = 'feedback-thanks';
        this.feedbackDraftType = null;
        this.requestScreenAnimation();
        this.render();
    }

    private renderFeedbackThanks(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-thanks';

        const iconWrap = document.createElement('div');
        iconWrap.className = 'votiq-thanks-icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        iconWrap.innerHTML = `<span class="votiq-thanks-icon-badge">${ICON_CHECK_SUCCESS}</span>`;

        const lead = document.createElement('p');
        lead.className = 'votiq-thanks-lead';
        lead.textContent =
            'Thank you! Your feedback was sent. We will follow up by email when there are updates.';

        const done = document.createElement('button');
        done.type = 'button';
        done.className = 'votiq-cta votiq-cta--block';
        done.textContent = 'Back to home';
        done.addEventListener('click', () => {
            this.screenTransition = 'back';
            this.goToHub();
        });

        wrap.appendChild(iconWrap);
        wrap.appendChild(lead);
        wrap.appendChild(done);

        return wrap;
    }

    private renderRoadmapDetail(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-detail votiq-detail--roadmap';

        const item = MOCK_ROADMAP.find((i) => i.id === this.selectedId);

        if (!item) {
            this.goToHub();

            return wrap;
        }

        const badgeClass =
            item.column === 'shipped'
                ? 'shipped'
                : item.column === 'in_progress'
                  ? 'progress'
                  : 'planned';

        const panel = document.createElement('div');
        panel.className = 'votiq-detail-panel votiq-detail-panel--roadmap';
        panel.innerHTML = `
      <span class="votiq-badge votiq-badge--${badgeClass}">${COLUMN_LABELS[item.column]}</span>
      <h3 class="votiq-detail-title">${escapeHtml(item.title)}</h3>
      <p class="votiq-detail-kicker">${item.votesCount} votes · public roadmap</p>
      <p class="votiq-detail-desc">Follow along as we ship. Vote counts reflect your community's interest.</p>
    `;
        wrap.appendChild(panel);

        return wrap;
    }

    private renderRoadmap(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-roadmap-screen';

        const sectionHead = document.createElement('div');
        sectionHead.className = 'votiq-roadmap-head';
        const headLabel = document.createElement('span');
        headLabel.className = 'votiq-list-toolbar-label';
        headLabel.textContent = 'At a glance';
        sectionHead.appendChild(headLabel);
        wrap.appendChild(sectionHead);

        const grid = document.createElement('div');
        grid.className = 'votiq-roadmap-stats';

        const statMod: Record<(typeof MOCK_ROADMAP)[0]['column'], string> = {
            planned: 'planned',
            in_progress: 'progress',
            shipped: 'shipped',
        };

        for (const col of ['planned', 'in_progress', 'shipped'] as const) {
            const count = MOCK_ROADMAP.filter((i) => i.column === col).length;
            const stat = document.createElement('div');
            stat.className = `votiq-stat votiq-stat--${statMod[col]}`;
            stat.innerHTML = `<div class="votiq-stat-label">${COLUMN_LABELS[col]}</div><div class="votiq-stat-value">${count}</div>`;
            grid.appendChild(stat);
        }

        wrap.appendChild(grid);

        const listHead = document.createElement('div');
        listHead.className = 'votiq-roadmap-list-head';
        const listLabel = document.createElement('span');
        listLabel.className = 'votiq-list-toolbar-label';
        listLabel.textContent = 'Up next';
        listHead.appendChild(listLabel);
        wrap.appendChild(listHead);

        for (const item of MOCK_ROADMAP) {
            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'votiq-roadmap-item';

            const badgeClass =
                item.column === 'shipped'
                    ? 'shipped'
                    : item.column === 'in_progress'
                      ? 'progress'
                      : 'planned';

            card.innerHTML = `
        <span class="votiq-roadmap-item-main">
          <span class="votiq-badge votiq-badge--${badgeClass}">${COLUMN_LABELS[item.column]}</span>
          <span class="votiq-roadmap-item-title">${escapeHtml(item.title)}</span>
        </span>
        <span class="votiq-roadmap-votes-pill" aria-label="${item.votesCount} votes">
          <span class="votiq-roadmap-votes-pill-inner">
            ${ICON_CHEVRON}
            <span class="votiq-roadmap-votes-num">${item.votesCount}</span>
          </span>
        </span>
      `;
            card.addEventListener('click', () => {
                this.screenTransition = 'forward';
                this.bodyScreen = 'roadmap-detail';
                this.selectedId = item.id;
                this.requestScreenAnimation();
                this.render();
            });
            wrap.appendChild(card);
        }

        return wrap;
    }

    private renderUpdates(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'votiq-updates-screen';

        const head = document.createElement('div');
        head.className = 'votiq-updates-head';
        const headLabel = document.createElement('span');
        headLabel.className = 'votiq-list-toolbar-label';
        headLabel.textContent = "What's new";
        head.appendChild(headLabel);
        wrap.appendChild(head);

        for (const release of MOCK_CHANGELOG) {
            const card = document.createElement('article');
            card.className = 'votiq-release-card';

            const cardHead = document.createElement('div');
            cardHead.className = 'votiq-release-card-head';
            cardHead.innerHTML = `
        <span class="votiq-release-version-pill">v${escapeHtml(release.version)}</span>
        <span class="votiq-release-date">${escapeHtml(release.date)}</span>
      `;
            card.appendChild(cardHead);

            if (release.title) {
                const titleEl = document.createElement('p');
                titleEl.className = 'votiq-release-card-title';
                titleEl.textContent = release.title;
                card.appendChild(titleEl);
            }

            const list = document.createElement('ul');
            list.className = 'votiq-changelog-items';

            for (const entry of release.entries) {
                const mod = changelogEntryModifier(entry.type);
                const li = document.createElement('li');
                li.className = 'votiq-changelog-item';
                li.innerHTML = `
          <span class="votiq-entry-pill votiq-entry-pill--${mod}">${escapeHtml(ENTRY_TYPE_LABELS[entry.type] ?? entry.type)}</span>
          <span class="votiq-changelog-item-title">${escapeHtml(entry.title)}</span>
        `;
                list.appendChild(li);
            }

            card.appendChild(list);
            wrap.appendChild(card);
        }

        return wrap;
    }
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function changelogEntryModifier(
    type: string,
): 'new' | 'improved' | 'deprecated' | 'removed' | 'fixed' | 'security' {
    if (
        type === 'new' ||
        type === 'improved' ||
        type === 'deprecated' ||
        type === 'removed' ||
        type === 'fixed' ||
        type === 'security'
    ) {
        return type;
    }

    return 'new';
}

let bodyInstance: VotiqWidget | null = null;
const containerInstances = new Map<HTMLElement, VotiqWidget>();

function unregisterInstance(
    widget: VotiqWidget,
    container: HTMLElement | null,
): void {
    if (container) {
        if (containerInstances.get(container) === widget) {
            containerInstances.delete(container);
        }

        return;
    }

    if (bodyInstance === widget) {
        bodyInstance = null;
    }
}

export function initWidget(options: VotiqInitOptions): VotiqInstance {
    const config = resolveConfig(options);
    const container = config.container;

    if (container) {
        containerInstances.get(container)?.destroy();
    } else {
        bodyInstance?.destroy();
    }

    const widget = new VotiqWidget(options);

    if (container) {
        containerInstances.set(container, widget);
    } else {
        bodyInstance = widget;
    }

    widget.mount();

    return buildInstanceApi(widget, container);
}

export function setWidgetContext(options: VotiqContextOptions | string): void {
    bodyInstance?.context(options);
}

function buildInstanceApi(
    widget: VotiqWidget,
    container: HTMLElement | null,
): VotiqInstance {
    return {
        update: (patch) => widget.update(patch),
        destroy: () => {
            widget.destroy();
            unregisterInstance(widget, container);
        },
        context: (options) => widget.context(options),
        open: () => widget.open(),
        close: () => widget.close(),
        toggle: () => widget.toggle(),
        isOpen: () => widget.isOpen(),
    };
}

export function updateWidget(patch: Partial<VotiqInitOptions>): void {
    bodyInstance?.update(patch);
}

export function destroyWidget(): void {
    bodyInstance?.destroy();
    bodyInstance = null;

    for (const instance of containerInstances.values()) {
        instance.destroy();
    }

    containerInstances.clear();
}

export function openWidget(): void {
    bodyInstance?.open();
}
